import React, { useState } from 'react';
import { Mail, X, ArrowLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { SmartMojoAuth } from './SmartMojoAuth';

export const LoginModal: React.FC = () => {
  const { isLoginOpen, setIsLoginOpen, phoneLogin } = useAuth();
  const [currentStep, setCurrentStep] = useState<'selection' | 'auth'>('selection');
  const [userType, setUserType] = useState<'new' | 'existing'>('existing');
  const [error, setError] = useState('');

  const handleSelection = (type: 'new' | 'existing') => {
    setUserType(type);
    setCurrentStep('auth');
    setError('');
  };

  const handleAuthSuccess = (userData: any) => {
    phoneLogin(userData);
    setIsLoginOpen(false);
    setError('');
    setCurrentStep('selection');
  };

  const handleBack = () => {
    setCurrentStep('selection');
    setError('');
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  if (!isLoginOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md border border-orange-100 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-orange-100 bg-stone-50/50">
          <div className="flex items-center gap-3">
            {currentStep !== 'selection' && (
              <button
                onClick={handleBack}
                className="text-stone-400 hover:text-stone-600 transition-colors mr-2"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <Mail size={18} className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-serif font-bold text-stone-900 leading-none mb-1">
                {currentStep === 'selection' ? 'Sign In' : userType === 'new' ? 'Create Account' : 'Welcome Back'}
              </h2>
              <p className="text-[10px] font-bold uppercase tracking-widest text-orange-500">
                {currentStep === 'selection' ? 'Devine Stone Registry' : 'Identity Verification'}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setIsLoginOpen(false);
              setError('');
              setCurrentStep('selection');
            }}
            className="text-stone-400 hover:text-stone-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              {error}
            </div>
          )}

          {currentStep === 'selection' && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h3 className="text-xl font-black text-stone-900 uppercase tracking-tighter">
                  Choose Path
                </h3>
                <p className="text-sm text-stone-500 max-w-[240px] mx-auto">
                  Access your exclusive stone collection dashboard
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleSelection('new')}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-black text-xs uppercase tracking-widest py-4 rounded-xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-orange-200 group"
                >
                  <Mail size={16} className="group-hover:scale-110 transition-transform" />
                  New User Registration
                </button>
                
                <button
                  onClick={() => handleSelection('existing')}
                  className="w-full border-2 border-orange-100 text-stone-700 hover:bg-orange-50 font-black text-xs uppercase tracking-widest py-4 rounded-xl transition-all flex items-center justify-center gap-3 group"
                >
                  <Mail size={16} className="text-orange-600 group-hover:scale-110 transition-transform" />
                  Registered User Login
                </button>
              </div>

              <p className="text-[10px] text-center text-stone-400 font-medium px-8">
                By continuing, you agree to Devine Stone's Terms of Service and Privacy Policy.
              </p>
            </div>
          )}

          {currentStep === 'auth' && (
            <SmartMojoAuth 
              userType={userType}
              onSuccess={handleAuthSuccess}
              onError={handleError}
              onBack={handleBack}
            />
          )}
        </div>
      </div>
    </div>
  );
};
